import { Segmented } from 'antd';
import { Column } from '@ant-design/plots';
import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { FiZap } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return `${date.getDate().toString().padStart(2, '0')}`;
};

const EnergyUtilizationGraph = ({ data, currentMonth, previousMonth, summary }) => {
  const { t } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [chartHeight, setChartHeight] = useState(200);
  const [chartKey, setChartKey] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setChartHeight(width < 400 ? 180 : 200);
        setChartKey(prev => prev + 1);
      }
    };

    const resizeObserver = new ResizeObserver(updateDimensions);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    updateDimensions();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!data || !currentMonth || !previousMonth || !data.data) {
    return null;
  }

  const chartRaw = data.data;
  const isCurrent = selectedMonth === currentMonth;
  const isNegative = summary?.changePercent < 0;
  const currentTotal = summary?.currentTotal ? summary.currentTotal / 1000 : 0;
  const previousTotal = summary?.previousTotal ? summary.previousTotal / 1000 : 0;
  const selectedMonthTotal = isCurrent ? currentTotal : previousTotal;

  const chartData = chartRaw
    .filter((item) => item[selectedMonth])
    .map((item) => ({
      date: formatDate(item.date),
      value: item[selectedMonth] / 1000,
      fullDate: item.date
    }));

  // Calculate optimal tick count based on data length
  const tickCount = Math.min(6, chartData.length);

  return (
    <div className="bg-white rounded-xl border border-gray-200 transition duration-200 p-4 h-full flex flex-col space-y-3" ref={containerRef}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <FiZap className="text-2xl text-yellow-500" />
          <div>
            <div className="font-semibold  text-sm">{t('dashboard:energyUtilizationTitle')}</div>
            <div className="text-sm font-bold text-gray-900">{Number(selectedMonthTotal).toFixed(4)} kW</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-nowrap ml-auto">
          {isCurrent && (
            <>
              <span className={`flex items-center font-semibold text-sm whitespace-nowrap ${isNegative ? 'text-red-600' : 'text-yellow-600'}`}> 
                {isNegative ? <ArrowDownOutlined /> : <ArrowUpOutlined />} 
                {summary && (Math.abs(summary.changeAmount) / 1000).toFixed(4)} kW
              </span>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold min-w-[40px] text-center whitespace-nowrap ${isNegative ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'}`}> 
                {isNegative ? '' : '+'}{summary && Math.abs(summary.changePercent)}%
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Segmented
          size="small"
          options={[
            { label: t(`dashboard:months.${previousMonth}`, { defaultValue: previousMonth }), value: previousMonth },
            { label: t(`dashboard:months.${currentMonth}`, { defaultValue: currentMonth }), value: currentMonth }
          ]}
          value={selectedMonth}
          onChange={setSelectedMonth}
          className="bg-gray-50"
        />
      </div>

      <div className="flex-grow" style={{ minHeight: chartHeight }}>
        <Column
          key={`${chartKey}-${selectedMonth}`}
          data={chartData}
          xField="date"
          yField="value"
          autoFit={true}
          axis={{
            y: {
              title: t('dashboard:energy'),
              grid: {
                line: {
                  style: {
                    stroke: '#f0f0f0',
                    lineWidth: 1,
                    lineDash: [4, 4],
                  },
                },
              },
              label: {
                formatter: (val) => `${Number(val).toFixed(4)}`,
                style: {
                  fontSize: 11,
                },
                offset: 10,
              },
            },
            x: {
              label: {
                style: {
                  fontSize: 10,
                  angle: 45,
                },
                offset: 25,
              },
              tickCount: tickCount,
              grid: null,
            },
          }}
          style={{
            fill: selectedMonth === currentMonth
              ? 'l(90) 0:#ffc954 1:#ededed'
              : 'l(90) 0:#ffc954 1:#ffffff ',
          }}
          areaStyle={{
            fillOpacity: 0.2,
          }}
          tooltip={{
            title: t('dashboard:tooltip.energy'),
            items: [{ 
              channel: 'y', 
              name: t('dashboard:energy'),
              valueFormatter: (val) => `${Number(val).toFixed(4)} kW` 
            }],
          }}
          inset={10}
          height={chartHeight}
          legend={false}
        />
      </div>
    </div>
  );
};

EnergyUtilizationGraph.propTypes = {
  data: PropTypes.object,
  currentMonth: PropTypes.string,
  previousMonth: PropTypes.string,
  summary: PropTypes.object,
};

export default EnergyUtilizationGraph; 